/**
 * Босоо баганат график — цаг хугацаа эсвэл дараалалтай ангилал
 * (сар, размер). Өөрчлөлт, оргил, уналтыг нэг харцаар уншуулна.
 *
 * Chart сан нэмэхгүй (CLAUDE.md — шинэ dependency). HTML + CSS:
 *  · шошго, tooltip нь HTML тул кирилл сунгагдахгүй
 *  · өнгө бүгд токен тул харанхуй горим өөрөө дагана
 *  · багана бүр `<button>` — Tab-аар шилжиж, дарж (утсанд) утгыг харна
 *
 * ⚠️ Юу ч ТООЦООЛОХГҮЙ — ирсэн утгыг л зурна. Тэнхлэгийн «цэвэр»
 *    дээд хязгаар нь зөвхөн зурах масштаб.
 */

import { useState } from 'react';
import { formatQty } from '@boots/domain';
import { Empty } from '../ui';

export interface ColumnPoint {
  key: string;
  /** Тэнхлэгийн богино шошго */
  label: string;
  /** Tooltip-ийн бүтэн нэр */
  title: string;
  value: number;
}

/** 0 · 1 · 2 · 5 · 10 … — тэнхлэгийн дээд хязгаарыг цэвэр тоонд тулгана. */
function niceCeil(max: number): number {
  if (max <= 4) return 4;
  const step = max / 4;
  const mag = 10 ** Math.floor(Math.log10(step));
  const unit = [1, 2, 2.5, 5, 10].find((m) => m * mag >= step) ?? 10;
  return unit * mag * 4;
}

export function ColumnChart({
  points,
  highlight,
  ariaLabel,
}: {
  points: ColumnPoint[];
  /** Онцлох баганын индекс (жишээ нь энэ сар). Бусад нь цайвар. */
  highlight?: number;
  ariaLabel: string;
}) {
  const [hover, setHover] = useState<number | null>(null);

  const peak = Math.max(0, ...points.map((p) => p.value));
  if (points.length === 0) return <Empty>Өгөгдөл алга</Empty>;
  // Бүгд 0 бол тэнхлэгтэй хоосон график — «Өгөгдөл алга» карт нь
  // график байгаа эсэхийг ч нууж, хуудас өөрчлөгдөөгүй мэт харагдуулдаг
  const blank = peak === 0;

  // 10% зай — оргилын шошго графикийн дээд хүрээнээс гарахгүй
  const top = niceCeil(peak * 1.1);
  const ticks = [top, top / 2, 0];
  const peakIdx = blank ? -1 : points.findIndex((p) => p.value === peak);
  const active = hover === null ? undefined : points[hover];
  const tipPct = hover === null ? 0 : ((hover + 0.5) / points.length) * 100;
  // 320px дээр 8-аас олон шошго багтахгүй — нүдийг үлдээж текстийг алгасна
  const dense = points.length > 8;

  return (
    <div className="flex flex-col gap-1.5" role="group" aria-label={ariaLabel}>
      <div className="flex gap-2">
        {/* Y тэнхлэг */}
        <div className="num flex h-[168px] w-8 shrink-0 flex-col justify-between text-right text-micro text-ink-3 sm:h-[200px]" aria-hidden>
          {ticks.map((t) => (
            <span key={t} className="-translate-y-1/2 first:translate-y-0 last:translate-y-0">{formatQty(t)}</span>
          ))}
        </div>

        <div className="relative h-[168px] min-w-0 flex-1 sm:h-[200px]" onPointerLeave={() => setHover(null)}>
          {/* Туслах шугам — нимгэн, тасралтгүй */}
          <div className="pointer-events-none absolute inset-0 flex flex-col justify-between" aria-hidden>
            {ticks.map((t) => (
              <span key={t} className="h-px bg-hair" />
            ))}
          </div>

          <div className="absolute inset-0 flex items-end">
            {points.map((p, i) => {
              const h = (p.value / top) * 100;
              const on = hover === i;
              const muted = highlight !== undefined && highlight !== i;
              return (
                <button
                  key={p.key}
                  type="button"
                  aria-label={`${p.title}: ${formatQty(p.value)} ш`}
                  onPointerEnter={() => setHover(i)}
                  onFocus={() => setHover(i)}
                  onBlur={() => setHover(null)}
                  onClick={() => setHover(i)}
                  className={`group relative flex h-full min-w-0 flex-1 items-end justify-center px-[2px]
                    outline-none ${on ? 'bg-row-hover' : ''}`}
                >
                  {i === peakIdx ? (
                    <span
                      className="num pointer-events-none absolute left-1/2 -translate-x-1/2 pb-0.5 text-micro text-ink-2"
                      style={{ bottom: `${h}%` }}
                      aria-hidden
                    >
                      {formatQty(p.value)}
                    </span>
                  ) : null}
                  <span
                    className={`block w-full max-w-6 rounded-t-[4px] transition ${
                      muted ? 'bg-chart-1/35' : 'bg-chart-1'
                    } ${on ? 'opacity-80' : ''} group-focus-visible:ring-2 group-focus-visible:ring-accent`}
                    // 0 биш утга 1px-ээс нимгэн болж алга болохгүй
                    style={{ height: p.value > 0 ? `max(2px, ${h}%)` : 0 }}
                  />
                </button>
              );
            })}
          </div>

          {blank ? (
            <span className="pointer-events-none absolute inset-0 grid place-items-center text-meta text-ink-3">
              Энэ хугацаанд бүртгэл алга
            </span>
          ) : null}

          {active ? (
            <div
              role="status"
              style={{
                left: `${tipPct}%`,
                transform: tipPct > 55 ? 'translateX(calc(-100% - 8px))' : 'translateX(8px)',
              }}
              className="pointer-events-none absolute top-0 flex flex-col gap-0.5 whitespace-nowrap
                rounded-well border border-hair bg-panel px-3 py-2 shadow-pop"
            >
              <span className="num text-h3">{formatQty(active.value)} ш</span>
              <span className="text-micro text-ink-2">{active.title}</span>
            </div>
          ) : null}
        </div>
      </div>

      {/* X тэнхлэг — HTML, SVG биш */}
      <div className="ml-10 flex text-micro text-ink-3" aria-hidden>
        {points.map((p, i) => (
          <span key={p.key} className="num min-w-0 flex-1 text-center">
            <span className={dense && i % 2 === 1 ? 'hidden sm:inline' : undefined}>{p.label}</span>
          </span>
        ))}
      </div>
    </div>
  );
}
